import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clientService from '../../api/clientService';
import { useNotifications } from '../../context/NotificationContext';
import BookingCard from '../common/BookingCard';
import Pagination from '../common/Pagination';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';
import ReviewModal from './Reviews';

const FILTERS = ['', 'AWAITING_APPROVAL', 'PENDING', 'CONFIRMED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED', 'REJECTED'];

export default function MyBookings() {
  const { t } = useTranslation();
  const notify = useNotifications();
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ content: [], totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reviewing, setReviewing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    clientService
      .listBookings({ page, size: 10, ...(status ? { status } : {}) })
      .then(setData)
      .catch((e) => notify.apiError(e))
      .finally(() => setLoading(false));
  }, [page, status, notify]);

  useEffect(() => { load(); }, [load]);

  const cancel = async (booking) => {
    if (!window.confirm(`${t('booking.actions.CANCELLED')} ?`)) return;
    setBusy(true);
    try {
      await clientService.cancelBooking(booking.id);
      notify.success(t('booking.status.CANCELLED'));
      load();
    } catch (e) {
      notify.apiError(e);
    } finally {
      setBusy(false);
    }
  };

  const actionsFor = (b) =>
    ['AWAITING_APPROVAL', 'PENDING', 'CONFIRMED'].includes(b.status)
      ? [{ status: 'CANCELLED', tone: 'danger' }]
      : [];

  return (
    <div className="page">
      <div className="container stack" style={{ gap: '1.25rem' }}>
        <div className="page-head">
          <h1>{t('client.myBookings')}</h1>
          <Link to="/technicians" className="btn accent">{t('client.newBooking')}</Link>
        </div>

        <div className="toolbar">
          {FILTERS.map((f) => (
            <button
              key={f || 'all'}
              className={`chip ${status === f ? 'active' : ''}`}
              onClick={() => { setStatus(f); setPage(0); }}
            >
              {f ? t(`booking.status.${f}`) : t('common.all')}
            </button>
          ))}
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : data.content.length === 0 ? (
          <EmptyState icon="calendar-x" title={t('client.noBookings')} />
        ) : (
          <div className="stack">
            {data.content.map((b) => (
              <BookingCard
                key={b.id}
                booking={b}
                perspective="client"
                actions={actionsFor(b)}
                busy={busy}
                onAction={(bk) => cancel(bk)}
                onReview={setReviewing}
              />
            ))}
          </div>
        )}
        <Pagination page={page} totalPages={data.totalPages} onChange={setPage} />
      </div>

      {reviewing && (
        <ReviewModal
          booking={reviewing}
          onClose={() => setReviewing(null)}
          onDone={() => { setReviewing(null); load(); }}
        />
      )}
    </div>
  );
}
